import React, { useMemo, useState } from 'react';
import { DollarSign, Loader2, UserCog } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import {
  useBrokerCommissionConfig,
  type RecipientCommissionRow,
} from '../../hooks/useBrokerCommissionConfig';
import { shouldShowRecipientGrossColumn } from '../../utils/recipientCommissionPresentation';

interface Props {
  companyId?: string;
  brokerDocId?: string;
  displayName?: string;
}

function formatCurrency(cents: number): string {
  return (cents / 100).toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatRate(bps: number | null): string {
  if (bps === null) return '—';
  return `${(bps / 100).toLocaleString('en-AU', { maximumFractionDigits: 2 })}%`;
}

function paymentLabel(value: string): string {
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function rowKey(row: RecipientCommissionRow): string {
  return row.rowId || `${row.brokerDocId}:${row.loanId}:${row.periodMonth}:${row.paymentType}`;
}

const RecipientCommissionReadOnlyView: React.FC<Props> = ({
  companyId,
  brokerDocId,
  displayName,
}) => {
  const [refreshing, setRefreshing] = useState(false);
  const {
    recipientView,
    recipientViewLoading,
    recipientViewError,
    refetchBigQueryData,
  } = useBrokerCommissionConfig(companyId, brokerDocId, {
    includeRecipientView: true,
    recipientOnly: true,
  });

  const roleLabel =
    recipientView?.role === 'credit_analyst'
      ? 'Credit Analyst'
      : recipientView?.role === 'broker_assistant'
        ? 'Broker Assistant'
        : 'Commission Recipient';
  const showGrossColumn = useMemo(
    () =>
      shouldShowRecipientGrossColumn(
        recipientView?.groups.flatMap((group) => group.rows) ?? [],
      ),
    [recipientView],
  );
  const rowCount =
    recipientView?.groups.reduce((sum, group) => sum + group.rows.length, 0) ?? 0;

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refetchBigQueryData();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={UserCog}
        title="My Commission Settings"
        subtitle={`${displayName || roleLabel} — ${roleLabel} read-only view`}
        onRefresh={handleRefresh}
        isRefreshing={refreshing}
      />

      {recipientViewLoading && (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
          <p className="text-gray-400 text-sm">Loading your commission payouts…</p>
        </div>
      )}

      {!recipientViewLoading && recipientViewError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-5 text-red-300">
          Commission payouts could not be loaded. No financial amounts are displayed.{' '}
          {recipientViewError}
        </div>
      )}

      {!recipientViewLoading && !recipientViewError && recipientView && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-800 border border-blue-500/30 rounded-lg p-5 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">
                    My Commission
                  </p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {formatCurrency(recipientView.recipientTotalCents)}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">Across the commission rows below</p>
                </div>
                <DollarSign className="w-8 h-8 text-blue-400" />
              </div>
            </div>
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-5 shadow-lg">
              <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">
                Included Rows
              </p>
              <p className="text-3xl font-bold text-white mt-1">{rowCount}</p>
              <p className="text-xs text-gray-500 mt-2">
                {recipientView.role === 'credit_analyst'
                  ? 'Only loans tagged to you are included'
                  : 'Calculated from your configured allocation'}
              </p>
            </div>
          </div>

          {recipientView.groups.length === 0 && (
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-10 text-center">
              <p className="text-white font-semibold">No active commission allocations</p>
              <p className="text-gray-500 text-sm mt-1">
                Ask an administrator to allocate you to a broker in Commission Settings.
              </p>
            </div>
          )}

          {recipientView.groups.map((group) => (
            <div
              key={group.brokerDocId}
              className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg"
            >
              <div className="p-5 border-b border-gray-700 flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-bold text-white">{group.brokerName}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {group.rows.length} commission {group.rows.length === 1 ? 'row' : 'rows'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wider text-gray-500">Your total</p>
                  <p className="text-lg font-bold text-green-400">
                    {formatCurrency(group.recipientTotalCents)}
                  </p>
                </div>
              </div>

              {group.rows.length === 0 ? (
                <div className="p-8 text-center text-gray-500 text-sm">
                  No tagged commission rows are available for this broker.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-900 border-b border-gray-700">
                      <tr>
                        {['Period', 'Loan ID', 'Customer', 'Loan Amount', 'Type'].map((heading) => (
                          <th
                            key={heading}
                            className="px-4 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider"
                          >
                            {heading}
                          </th>
                        ))}
                        {showGrossColumn && (
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">
                            Commission
                          </th>
                        )}
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Effective Rate
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          My Commission
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.rows.map((row) => (
                        <tr key={rowKey(row)} className="border-b border-gray-800 hover:bg-vlt-dark/50">
                          <td className="px-4 py-3 text-gray-400 text-sm">{row.periodMonth}</td>
                          <td className="px-4 py-3 text-blue-400 font-mono text-sm">{row.loanId}</td>
                          <td className="px-4 py-3 text-white text-sm">
                            {row.customerName || 'Unknown'}
                          </td>
                          {/* Settled amount on upfront, current balance on trail.
                              Absent (hideLoanAmounts) or blank renders as an em
                              dash, never $0. */}
                          <td className="px-4 py-3 text-gray-300 text-sm tabular-nums">
                            {row.loanValueCents == null ? (
                              <span className="text-gray-600">—</span>
                            ) : (
                              formatCurrency(row.loanValueCents)
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-300 text-sm">
                            {paymentLabel(row.paymentType)}
                          </td>
                          {showGrossColumn && (
                            <td className="px-4 py-3 text-right text-gray-300 font-medium">
                              {formatCurrency(row.grossCommissionCents!)}
                            </td>
                          )}
                          <td className="px-4 py-3 text-right text-blue-300 font-medium">
                            {formatRate(row.effectiveRateBps)}
                          </td>
                          <td
                            className={`px-4 py-3 text-right font-bold ${
                              row.recipientCommissionCents < 0 ? 'text-rose-400' : 'text-green-400'
                            }`}
                          >
                            {formatCurrency(row.recipientCommissionCents)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default RecipientCommissionReadOnlyView;
